const domainOf = (id) => id.slice(0, id.indexOf('.'));
// ---------- states ----------
export function packState(s) {
    let hasAttrs = false;
    for (const _k in s.a) {
        hasAttrs = true;
        break;
    }
    if (s.lu !== s.lc)
        return [s.s, s.lc, hasAttrs ? s.a : 0, s.lu];
    return hasAttrs ? [s.s, s.lc, s.a] : [s.s, s.lc];
}
export function unpackState(p) {
    return { s: p[0], a: p[2] || {}, lc: p[1], lu: p[3] ?? p[1] };
}
export function packStates(states) {
    const out = {};
    for (const id in states)
        out[id] = packState(states[id]);
    return out;
}
export function unpackStates(states) {
    const out = {};
    for (const id in states)
        out[id] = unpackState(states[id]);
    return out;
}
// ---------- model ----------
const ROLE_PACK = { control: 'c', reading: 'r', setting: 's', diagnostic: 'd' };
const ROLE_UNPACK = { c: 'control', r: 'reading', s: 'setting', d: 'diagnostic' };
export function packMeta(m) {
    const p = { n: m.name, p: m.platform };
    if (m.shortName !== undefined && m.shortName !== m.name)
        p.sn = m.shortName;
    if (m.deviceId)
        p.d = m.deviceId;
    if (m.roomId)
        p.r = m.roomId;
    if (m.alsoRoomIds?.length)
        p.ar = m.alsoRoomIds;
    if (m.category)
        p.c = m.category;
    if (m.deviceClass)
        p.k = m.deviceClass;
    if (m.unit)
        p.u = m.unit;
    if (m.icon)
        p.i = m.icon;
    if (m.readOnly)
        p.ro = 1;
    if (m.readOnlyReason)
        p.rr = m.readOnlyReason;
    if (m.dangerous)
        p.x = 1;
    if (m.scale !== undefined)
        p.sc = m.scale;
    if (m.adminOnly)
        p.ao = 1;
    if (m.ownerOnly)
        p.oo = 1;
    if (m.requiresPrivilege)
        p.rp = m.requiresPrivilege;
    if (m.role)
        p.rl = ROLE_PACK[m.role];
    if (m.house)
        p.h = 1;
    if (m.warnings?.length)
        p.wn = m.warnings;
    if (m.hint)
        p.hn = m.hint;
    if (m.group)
        p.gp = m.group;
    if (m.writtenBy?.length)
        p.wb = m.writtenBy;
    if (m.labelled)
        p.lb = 1;
    return p;
}
export function unpackMeta(id, p) {
    const m = {
        id, name: p.n, domain: domainOf(id), deviceId: p.d ?? null, roomId: p.r ?? null, platform: p.p,
        category: p.c ?? null, deviceClass: p.k ?? null, unit: p.u ?? null, icon: p.i ?? null,
    };
    if (p.sn !== undefined)
        m.shortName = p.sn;
    if (p.ar?.length)
        m.alsoRoomIds = p.ar;
    if (p.ro)
        m.readOnly = true;
    if (p.rr)
        m.readOnlyReason = p.rr;
    if (p.x)
        m.dangerous = true;
    if (p.sc !== undefined)
        m.scale = p.sc;
    if (p.ao)
        m.adminOnly = true;
    if (p.oo)
        m.ownerOnly = true;
    if (p.rp)
        m.requiresPrivilege = p.rp;
    if (p.rl)
        m.role = ROLE_UNPACK[p.rl];
    if (p.h)
        m.house = true;
    if (p.wn?.length)
        m.warnings = p.wn;
    if (p.hn)
        m.hint = p.hn;
    if (p.gp)
        m.group = p.gp;
    if (p.wb?.length)
        m.writtenBy = p.wb;
    if (p.lb)
        m.labelled = true;
    return m;
}
export function packDevice(d) {
    const p = { n: d.name };
    if (d.shortName !== undefined && d.shortName !== d.name)
        p.sn = d.shortName;
    if (d.manufacturer)
        p.mf = d.manufacturer;
    if (d.model)
        p.md = d.model;
    if (d.roomId)
        p.r = d.roomId;
    if (d.system)
        p.sy = d.system;
    if (d.type)
        p.t = d.type;
    if (d.icon)
        p.ic = d.icon;
    return p;
}
export function unpackDevice(id, p) {
    const d = { id, name: p.n, manufacturer: p.mf ?? null, model: p.md ?? null, roomId: p.r ?? null, system: p.sy ?? null };
    if (p.sn !== undefined)
        d.shortName = p.sn;
    if (p.t)
        d.type = p.t;
    if (p.ic)
        d.icon = p.ic;
    return d;
}
export function packModel(m) {
    const entities = {};
    for (const id in m.entities)
        entities[id] = packMeta(m.entities[id]);
    const devices = {};
    for (const id in m.devices)
        devices[id] = packDevice(m.devices[id]);
    return { ...m, entities, devices };
}
export function unpackModel(p) {
    const entities = {};
    for (const id in p.entities)
        entities[id] = unpackMeta(id, p.entities[id]);
    const devices = {};
    for (const id in p.devices)
        devices[id] = unpackDevice(id, p.devices[id]);
    return { ...p, entities, devices };
}
// ---------- access ----------
const capKey = (c) => [...c].sort().join(',');
/** `ids` = the entity ids of the model sent alongside (every visible entity). */
export function packAccess(a, ids) {
    const { entities, ...rest } = a;
    const counts = new Map();
    const all = [...ids];
    for (const id of all) {
        const caps = entities[id] ?? [];
        const k = capKey(caps);
        const c = counts.get(k);
        if (c)
            c.n++;
        else
            counts.set(k, { n: 1, caps: [...caps].sort() });
    }
    let best = { n: 0, caps: [] };
    for (const c of counts.values())
        if (c.n > best.n)
            best = c;
    const bestKey = capKey(best.caps);
    const ex = {};
    for (const id of all) {
        const caps = entities[id] ?? [];
        if (capKey(caps) !== bestKey)
            ex[id] = caps;
    }
    return { ...rest, ed: best.caps, ex };
}
export function unpackAccess(p, ids) {
    const { ed, ex, ...rest } = p;
    const entities = {};
    for (const id of ids)
        entities[id] = ex[id] ?? ed;
    for (const id in ex)
        entities[id] = ex[id];
    // Entities with no capabilities are invisible.
    for (const id in entities)
        if (!entities[id].length)
            delete entities[id];
    return { ...rest, entities };
}
//# sourceMappingURL=wire.js.map